import type { components } from '@/api/schema'

// Admin panel schemas (Admin*) are EXCLUDED: they reuse the same JSON:API
// `type` strings (user, organization, membership) with operator-facing
// payloads, so letting them in would fork every Resource<'user'> etc. into a
// union and break narrowing app-wide. The admin composables type against
// components['schemas']['Admin…'] directly and never join this system.
type AllSchemas = components['schemas'][Exclude<keyof components['schemas'], `Admin${string}`>]

// Inferred Union of ALL resources in the schema directly from the backend
export type AllResources = Extract<AllSchemas, { id: string; type: string; attributes: unknown }>

// The generic Resource type
export type Resource<T extends AllResources['type'] = AllResources['type']> = Extract<AllResources, { type: T }>

export type FindResponseForResourceType<TType extends AllResources['type']> = {
  [K in keyof components['schemas']]: components['schemas'][K] extends { data: Array<infer Item>; included?: unknown } | { data: Array<infer Item>; meta?: unknown }
    ? Item extends { type: infer T }
      ? T extends TType
        ? components['schemas'][K]
        : never
      : never
    : never
}[keyof components['schemas']]

export type Included<TType extends AllResources['type']> = 
  FindResponseForResourceType<TType> extends { included?: infer I }
    ? I extends Array<infer Item>
      ? Item extends Resource<infer ItemType>
        ? ItemType
        : never
      : never
    : never

export const isType = <T extends AllResources['type']>(
  r: Resource | null | undefined, 
  type: T
): r is Resource<T> => {
  return r != null && r.type === type;
}

/**
 * Safely find and type-narrow a resource from a JSON:API included array.
 */
export function findResource<T extends AllResources['type']>(
  included: Resource[] | undefined | null,
  id: string | undefined,
  type: T
): Resource<T> | null {
  if (!included || !id) return null
  for (const r of included) {
    if (r.id === id && isType(r, type)) {
      return r;
    }
  }
  return null
}

export interface PaginationMeta {
  total: number;
}

/**
 * Merges two arrays of JSON:API resources, ensuring uniqueness by ID and Type.
 * The new resources will overwrite older resources with the same ID/Type.
 */
/**
 * A held resource, brought up to date from a fresh copy that arrived as an
 * INCLUDE of some other write (e.g. an invoice PATCH with `include[job]`).
 *
 * ⚠ ATTRIBUTES ONLY. A sideloaded resource carries its to-many linkage as `[]`
 * — the server only links what it loaded — so taking its relationships would
 * make a job read as having no visits and no proposals. The held linkage is
 * kept; a write that RE-LINKS records is not described by this and still
 * needs a refetch.
 */
export function absorbAttributes<T extends { id: string; attributes?: unknown }>(held: T, fresh: T): T {
  return held.id === fresh.id ? { ...held, attributes: fresh.attributes } : held
}

export function mergeResources<T extends AllResources>(
  existing: T[] | null | undefined,
  incoming: readonly T[] | null | undefined
): T[] {
  const map = new Map<string, T>()
  
  for (const item of (existing || [])) {
    map.set(`${item.type}:${item.id}`, item)
  }
  
  for (const item of (incoming || [])) {
    map.set(`${item.type}:${item.id}`, item as T)
  }
  
  return Array.from(map.values())
}